import React, { useState } from 'react';
import { Check, ChevronDown, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import UserAvatar from './UserAvatar';

/**
 * Multi-select dropdown for picking multiple users.
 * - users: [{id, name}]
 * - value: string[] (selected user ids)
 * - onChange: (ids: string[]) => void
 */
const MultiUserSelect = ({ users = [], value = [], onChange, placeholder, className = '', size = 'md' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const safeValue = Array.isArray(value) ? value : [];
  const selectedUsers = users.filter(u => safeValue.includes(u.id));

  const toggleUser = (id) => {
    if (safeValue.includes(id)) {
      onChange(safeValue.filter(v => v !== id));
    } else {
      onChange([...safeValue, id]);
    }
  };

  const removeUser = (e, id) => {
    e.stopPropagation();
    onChange(safeValue.filter(v => v !== id));
  };

  const heightClass = size === 'sm' ? 'h-9 text-xs' : 'h-12 text-sm';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="multi-user-select-trigger"
          className={`w-full ${heightClass} px-3 rounded-md border border-zinc-200 bg-white hover:border-zinc-300 transition-colors flex items-center justify-between gap-2 ${className}`}
        >
          {selectedUsers.length === 0 ? (
            <span className="text-zinc-400 truncate flex items-center gap-2">
              <Users className="w-4 h-4" />
              {placeholder || t('plan.selectAssignees', 'Personen auswählen')}
            </span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap overflow-hidden">
              {selectedUsers.slice(0, 3).map(u => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1 bg-zinc-100 rounded-full pl-1 pr-2 py-0.5"
                >
                  <UserAvatar user={u} size="xs" />
                  <span className="text-xs text-zinc-700 truncate max-w-[80px]">{u.name?.split(' ')[0]}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => removeUser(e, u.id)}
                    className="hover:bg-zinc-200 rounded-full p-0.5"
                  >
                    <X className="w-2.5 h-2.5 text-zinc-500" />
                  </span>
                </span>
              ))}
              {selectedUsers.length > 3 && (
                <span className="text-xs text-zinc-500 font-medium">+{selectedUsers.length - 3}</span>
              )}
            </div>
          )}
          <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1 max-h-72 overflow-y-auto" align="start">
        {users.length === 0 ? (
          <div className="px-3 py-4 text-sm text-zinc-500 text-center">{t('plan.noUsers', 'Keine Benutzer')}</div>
        ) : (
          users.map(u => {
            const isSelected = safeValue.includes(u.id);
            return (
              <button
                type="button"
                key={u.id}
                data-testid={`multi-user-option-${u.id}`}
                onClick={() => toggleUser(u.id)}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-left text-sm hover:bg-zinc-50 transition-colors ${
                  isSelected ? 'bg-zinc-50' : ''
                }`}
              >
                <Checkbox checked={isSelected} className="pointer-events-none" />
                <UserAvatar user={u} size="sm" />
                <span className="flex-1 truncate text-zinc-800">{u.name}</span>
                {isSelected && <Check className="w-4 h-4 text-[#D93025]" />}
              </button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MultiUserSelect;
